import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { receiptsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import { DocHeader, DocFooter } from '../components/DocHeader';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const METHOD_LABELS = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', online: 'Online', other: 'Other' };

export default function ReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideHeaderFooter, setHideHeaderFooter] = useState(false);
  const printRef = useRef();

  const cur = settings.currency || 'BDT';
  const primary = settings.primary_color || '#2563eb';

  useEffect(() => {
    receiptsAPI.getOne(id)
      .then(res => setReceipt(res.data))
      .catch(() => toast.error('Receipt not found'))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: receipt ? `Receipt-${receipt.receipt_number}` : 'Receipt',
    onAfterPrint: () => setHideHeaderFooter(false),
    pageStyle: `
      @page { size: A4; margin: 0; }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        body { margin: 0; padding: 0; }
      }
    `,
  });

  const handleDelete = async () => {
    if (!window.confirm(`Delete receipt ${receipt.receipt_number}?`)) return;
    try {
      await receiptsAPI.delete(id);
      toast.success('Receipt deleted');
      navigate('/receipts');
    } catch { toast.error('Delete failed'); }
  };

  const fmtDate = d => d ? format(new Date(d), 'dd.MM.yyyy') : '-';

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>;
  if (!receipt) return <div style={{ padding: 40 }}>Receipt not found. <Link to="/receipts">Go back</Link></div>;

  const emptyRows = 6;

  return (
    <div className="doc-wrapper">
      <Link to="/receipts" className="back-link">← Back to Receipts</Link>

      <div className="page-actions">
        <button className="btn btn-outline" onClick={handlePrint}>🖨 Print / PDF</button>
        <button className="btn btn-outline" onClick={() => { setHideHeaderFooter(true); setTimeout(handlePrint, 100); }}>🖨 No Header/Footer</button>
        <button className="btn btn-danger" onClick={handleDelete}>🗑 Delete</button>
      </div>

      {/* Printable Document */}
      <div ref={printRef}>
        <div style={{
          background: '#fff',
          width: '100%',
          minHeight: '297mm',
          fontFamily: "'DM Sans', sans-serif",
          display: 'flex',
          flexDirection: 'column',
        }}>

          {/* HEADER */}
          {hideHeaderFooter ? (
            <div style={{ height: 120, width: '100%' }} />
          ) : (
            <DocHeader docType="RECEIPT" />
          )}

          {/* RECEIVED FROM + META */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '20px 48px' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', borderBottom: '1.5px solid #e2e8f0', paddingBottom: 4, marginBottom: 8 }}>
                Received From
              </div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{receipt.client_name}</div>
              {receipt.client_address && <div style={{ color: '#64748b', marginTop: 2, fontSize: 14 }}>{receipt.client_address}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Date:</span>
                <span style={{ fontWeight: 600, color: primary }}>{fmtDate(receipt.payment_date)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Receipt No.</span>
                <span style={{ fontWeight: 700 }}>{receipt.receipt_number}</span>
              </div>
              {receipt.bill_number && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Bill No.</span>
                  <span style={{ fontWeight: 600 }}>{receipt.bill_number}</span>
                </div>
              )}
              {receipt.payment_method && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Method:</span>
                  <span>{METHOD_LABELS[receipt.payment_method] || receipt.payment_method}</span>
                </div>
              )}
              {receipt.reference_number && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Ref:</span>
                  <span>{receipt.reference_number}</span>
                </div>
              )}
            </div>
          </div>

          {/* ITEMS TABLE */}
          <div style={{ padding: '0 48px 20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <thead style={{ backgroundColor: primary }}>
                <tr style={{ backgroundColor: primary }}>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', width: 60, textAlign: 'center' }}>SL.</th>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', textAlign: 'left' }}>Description</th>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', width: 160, textAlign: 'right' }}>Amount ({cur})</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
                  <td style={{ padding: '13px 16px', textAlign: 'center', color: '#64748b', fontSize: 14 }}>1</td>
                  

<td style={{ padding: '13px 16px', fontSize: 14 }}>
  <div>{receipt.notes || 'Payment received'}</div>
  {receipt.bill_number && (
    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Against Bill: {receipt.bill_number}</div>
  )}
</td>

                  <td style={{ padding: '13px 16px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                    {parseFloat(receipt.amount).toLocaleString()}
                  </td>
                </tr>
                {Array.from({ length: emptyRows }).map((_, i) => (
                  <tr key={`e${i}`} style={{ borderBottom: '1px solid #e2e8f0', background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                    <td style={{ padding: '13px 16px' }}>&nbsp;</td>
                    <td style={{ padding: '13px 16px' }}></td>
                    <td style={{ padding: '13px 16px' }}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TOTAL ROW */}
          <div style={{ padding: '0 48px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', marginTop: 4 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Total Received</div>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800, fontSize: 18 }}>
              {cur} {parseFloat(receipt.amount).toLocaleString()}
            </div>
          </div>

          {/* NOTES */}
          {receipt.notes && (
            <div style={{ padding: '0 48px 20px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>{receipt.notes}</div>
            </div>
          )}

          {/* SPACER — pushes footer to bottom */}
          <div style={{ flex: 1 }} />

          {/* FOOTER */}
          {hideHeaderFooter ? (
            <div style={{ height: 80, width: '100%' }} />
          ) : (
            <DocFooter />
          )}

        </div>
      </div>
    </div>
  );
}